import { useContext } from 'react'
import axios, { AxiosResponse } from 'axios'
import { EmptyApiResult, ApiResult } from 'api'
import { usePageNotifications } from './usePageNotifications'
import { useLogger } from './useLogger'
import { useAuth } from './useAuth'
import { AuthContext } from 'contexts'
import { SESSION_KEY } from 'contexts/AuthContext'

const API_CALLS_THAT_SKIP_SESSION_REFRESH = [
  '/api/customerAgencies/health',
  '/api/health',
]

export const useApiClient = () => {
  const xsrfCookie = document.cookie.replace(
    /(?:(?:^|.*;\s*)CSRF-TOKEN\s*\=\s*([^;]*).*$)|^.*$/,
    '$1',
  )

  const { performLogoff } = useAuth()
  const { dispatch } = useContext(AuthContext)
  const axiosInstance = axios.create({
    // this adds the X-XSRF-TOKEN header
    withCredentials: true,
    headers: {
      // this header triggers WebAdmin to send 401 responses instead of redirecting us to the login screen
      'X-Requested-With': 'XMLHttpRequest',
      RequestVerificationToken: xsrfCookie,
    },
  })

  axiosInstance.interceptors.response.use(
    (response) => {
      if (
        API_CALLS_THAT_SKIP_SESSION_REFRESH.filter((apiRoute) =>
          response.config.url?.startsWith(apiRoute),
        ).length === 0
      ) {
        dispatch({
          type: 'ApiActivity',
          payload: Date.now(),
        })
      }
      return response
    },
    (error) => {
      if (error.response.status === 401) {
        // check to see if the browser thought they should have been logged in
        // if we did not have a client-side session then don't do the logout flow
        if (!sessionStorage.getItem(SESSION_KEY)) return null

        performLogoff()
        return null
      } else {
        return Promise.reject(error)
      }
    },
  )
  return axiosInstance
}

export const useInvokeApi = () => {
  const { logError } = useLogger()
  const { dispatch: dispatchNotification } = usePageNotifications()

  return async <TResult>(
    action: () => Promise<AxiosResponse<EmptyApiResult | ApiResult<TResult>>>,
    errorMessage: string,
    onResult?: (result: TResult) => void,
    onSuccess?: () => void,
    onFailure?: () => void,
    dispatchError?: boolean,
  ): Promise<TResult | null> => {
    try {
      const response = await action()
      if (!response) return null

      if (response.status == 200 && response.data.isSuccess) {
        if ('data' in response.data && onResult) {
          onResult(response.data.data)
          return response.data.data
        }

        if (onSuccess) {
          onSuccess()
        }
        if ('data' in response.data) {
          return response.data.data
        }
      } else {

        if (dispatchError)
        {
          dispatchNotification({
            type: 'SetError',
            payload: errorMessage,
          })
        }

        logError(
          'Error: ' +
            errorMessage +
            ' From response ' +
            response.status.toString() +
            ': ' +
            response.statusText,
        )

        if (onFailure)
          onFailure()
      }
    } catch (error) {
      logError(error)

      if (dispatchError)
      {
        dispatchNotification({
          type: 'SetError',
          payload: errorMessage,
        })
      }

      if (onFailure)
        onFailure()
    }

    return null
  }
}
